import {TranslocoService} from '@ngneat/transloco';
import {SymptomsService} from '../../../../services/symptoms.service';
import {CommonCsvContentBuilder} from '../common-csv-content-builder';

export class ChildrenVisitsCsvContentBuilder extends CommonCsvContentBuilder {

    constructor(private visitData: any,
                private symptomService: SymptomsService,
                private translocoService: TranslocoService) {
        super();
    }

    build(): string {
        return this.visitData.map(visit => [
            visit.caseId ?? '',
            visit.muac ?? '',
            this.translateMarker(visit.marker),
            this.formatDate(visit.date) ?? '',
            visit.nextVisit ?? '',
            this.translateBoolean(visit.fowardToHospital),
            visit.week ?? '',
            visit.healthAssistant ? visit.healthAssistant.uid : '',
            this.getFormattedHealthAssistantName(visit),
            visit.province ?? '',
            visit.county ?? '',
            visit.organizationName ?? '',
            visit.order ?? '',
            this.sanitizeText(visit.comments),
            this.getFormattedInstructions(visit.instructions),
            this.getFormattedSymptoms(visit.symptoms)
        ].join(';')).join('\r\n');
    }

    private getFormattedInstructions(instructions: Array<string>) {
        if (!instructions || instructions.length === 0) {
            return '';
        }
        return instructions.map(instruction => this.sanitizeText(instruction)).join('/');
    }

    private getFormattedHealthAssistantName(visit: any) {
        if (!visit || !visit.healthAssistant) {
            return '';
        }
        return `${visit.healthAssistant.firstName} ${visit.healthAssistant.lastName}`;
    }

    private translateMarker(marker: string) {
        if (!marker) {
            return '';
        }
        switch (marker) {
            case 'acute-malnutrition':
                return 'Desnutrición aguda';
            case 'outside-risk-zone':
                return 'Niño fuera de la zona de riesgo';
            case 'outsite-risk-zone':
                return 'Niño fuera de la zona de riesgo';
            case 'risk-of-acute-malnutrition':
                return 'Riesgo de desnutrición aguda';
            case 'moderate-acute-malnutrition':
                return 'Desnutrición aguda moderada';
            case 'severe-acute-malnutrition':
                return 'Desnutrición aguda severa';
            case 'healed':
                return 'Niño recuperado';
            case 'no-measured':
                return 'No medido';
        }
    }

    private translateAlertIcon(alertIcon: string) {
        if (!alertIcon) {
            return '';
        }
        switch (alertIcon) {
            case 'calendar-icon':
                return 'Tratamiento interrumpido';
            case 'exclamation-icon':
                return 'Casos sin progreso';
            case 'hospital-icon':
                return 'Hospital';
        }
    }

    private translateBoolean(boo: boolean) {
        if (boo === null || boo === undefined) {
            return '';
        }
        if (boo) {
            return 'Sí';
        }
        return 'No';
    }

    private getFormattedSymptoms(symptoms: string[]) {
        if (!symptoms) {
            return;
        }
        return symptoms
            .filter(symptom => !!symptom && symptom.trim().length > 0)
            .map(symptom => {
                const symptomSanitized = this.sanitizeText(symptom);
                const checkedSymptom = this.checkSymptom(symptomSanitized);
                if (checkedSymptom.mustTranslate) {
                    return this.translocoService.translate(
                        this.symptomService.getByKey(symptom)
                    );
                }
                return symptom;
            });
    }

    private checkSymptom(value: string): CheckedSymptom {
        const isUpperCase = value === value.toUpperCase();
        const mustTranslate = isUpperCase;
        const symptom = mustTranslate
            ? this.toCamelCase(value)
            : value;
        return {symptom, mustTranslate};
    }

    private toCamelCase(input: string): string {
        const parts = input.split('_');
        return parts
            .map((word, index) => {
                const lower = word.toLowerCase();
                return index === 0 ? lower : lower.charAt(0).toUpperCase() + lower.slice(1);
            })
            .join('');
    }

    private formatDate(dateString: string): string {
        if (!dateString) {
            return '';
        }
        if (dateString.includes('T')) {
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
        }
        return dateString;
    }

}

interface CheckedSymptom {
    symptom: string;
    mustTranslate: boolean;
}
